import type { SitePage } from './types';

/**
 * Single source of truth for static/page SEO + discoverability flags.
 * Blog posts stay in src/data/blogs/*.json (see CONTENT.md).
 */
export const SITE_PAGES: SitePage[] = [
  {
    path: '/',
    label: 'Home',
    kind: 'home',
    navPrimary: true,
    navOrder: 10,
    llms: 'core',
    llmsSummary:
      'Overview of web development, ecommerce, AI automation, integrations, and support services.',
    priority: 1,
    seo: {
      title: 'Web & product engineering for global teams',
      description:
        'Web development and product engineering for global teams—React, Next.js, ecommerce, Magento, AI automation, WordPress, ERP, and long-term support.',
      keywords:
        'web development company, product engineering agency, React development, Magento ecommerce, WordPress development, AI automation, website maintenance, global software partner, Plux Infosystems',
      image: '/img/webdevelopment.png',
      imageAlt: 'Web design and product engineering illustration',
    },
  },
  {
    path: '/services',
    label: 'Services',
    kind: 'service',
    navPrimary: true,
    footerService: true,
    navOrder: 20,
    llms: 'core',
    llmsSummary:
      'Main service index for development, automation, ecommerce, analytics, and maintenance.',
    priority: 0.9,
    seo: {
      title: 'Web, ecommerce, AI & integration services',
      description:
        'Senior-led web development, AI automation, Magento/WooCommerce, WordPress Sage, CRM/ERP integrations, analytics, and long-term support.',
      keywords:
        'web development services, product engineering agency, AI automation services, ecommerce development, Magento development, WordPress Sage development, CRM integration, ERP development, Core Web Vitals optimization, React development, Next.js agency, Node.js API, Python development, website maintenance',
      image: '/img/webdevelopment.png',
      imageAlt: 'Web development and product engineering services illustration',
    },
  },
  {
    path: '/about',
    label: 'About',
    kind: 'company',
    navPrimary: true,
    footerCompany: true,
    navOrder: 30,
    llms: 'core',
    llmsSummary: 'Company overview, delivery approach, and team positioning.',
    priority: 0.8,
    seo: {
      title: 'About our web & product engineering team',
      description:
        'India-based web and product engineering team for React, Magento, WordPress, AI automation, CRM/ERP integrations, and long-term support.',
      keywords:
        'about Plux Infosystems, web development company Gautam Buddha Nagar, product engineering team India, ecommerce development team, AI automation team, CRM ERP integration partner, WordPress Magento developers',
      image: '/img/webdevelopment.png',
      imageAlt: 'Plux Infosystems technical team and company overview',
    },
  },
  {
    path: '/portfolio',
    label: 'Portfolio',
    kind: 'portfolio',
    navPrimary: true,
    footerCompany: true,
    navOrder: 40,
    llms: 'core',
    llmsSummary:
      'Examples of education, ecommerce, B2B marketplace, AI, Magento, WordPress, Laravel, React, and React Native work.',
    priority: 0.8,
    seo: {
      title: 'Portfolio: web, ecommerce, AI & education',
      description:
        'Selected work across education websites, ecommerce, marketplaces, AI interfaces, Magento, WordPress, Laravel, React, and React Native.',
      keywords:
        'web development portfolio, education website development, ecommerce development portfolio, Magento portfolio, WordPress agency Gautam Buddha Nagar, Laravel development, React portfolio, React Native apps, AI automation portfolio, B2B marketplace development, CRM integration projects',
      image: '/img/portfolio-sae-edu-au.png',
      imageAlt: 'Plux Infosystems portfolio — education and web development work',
    },
  },
  {
    path: '/blog',
    label: 'Blog',
    kind: 'blog-index',
    navPrimary: true,
    footerCompany: true,
    navOrder: 50,
    llms: 'core',
    llmsSummary:
      'Practical guides on web development, ecommerce, CRM integrations, analytics, WordPress, Magento, SEO, and AI automation.',
    priority: 0.8,
    seo: {
      title: 'Insights on web, ecommerce, SEO & AI',
      description:
        'Practical guides on web development, ecommerce, CRM integrations, analytics, WordPress/Magento, education search, and SEO delivery.',
      keywords:
        'web development blog, ecommerce development insights, Magento SEO, WordPress performance, CRM integration guides, GTM analytics, education website SEO, AI automation articles',
    },
  },
  {
    path: '/career',
    label: 'Career',
    kind: 'company',
    navPrimary: true,
    navOrder: 60,
    llms: 'core',
    llmsSummary: 'Career application page for technical and digital roles.',
    priority: 0.6,
    seo: {
      title: 'Careers — apply online',
      description:
        'Apply for web development, web design, ecommerce, digital marketing, and product engineering roles at Plux Infosystems.',
      keywords:
        'Plux Infosystems career, web developer jobs Gautam Buddha Nagar, apply web designer job, ecommerce developer career',
    },
  },
  {
    path: '/getintouch',
    label: 'Contact',
    kind: 'company',
    navPrimary: true,
    footerCompany: true,
    navOrder: 70,
    llms: 'core',
    llmsSummary: 'Contact Plux Infosystems by phone, email, or project brief.',
    priority: 0.8,
    seo: {
      title: 'Contact us for web, ecommerce & support',
      description:
        'Contact our Gautam Buddha Nagar team for web development, ecommerce, CRM integrations, maintenance, and AI automation. Email or chat — no calls.',
      keywords:
        'contact web development company Gautam Buddha Nagar, hire ecommerce developer India, website maintenance support, CRM integration consultation, AI automation agency, Plux Infosystems contact',
    },
  },
  {
    path: '/leadership',
    label: 'Leadership',
    kind: 'company',
    footerCompany: true,
    navOrder: 80,
    llms: 'core',
    llmsSummary: 'Directors and leadership team.',
    priority: 0.7,
    seo: {
      title: 'Leadership: Founder, CTO & design head',
      description:
        'Meet leadership: Kailash Chandra, Founder, MD and CTO; and Laxmi Chandra, Co-Founder, Director and Head of Product Design.',
      keywords:
        'Plux Infosystems leadership, Kailash Chandra founder CTO, Laxmi Chandra Raj Kumari product design, managing director, co-founder',
    },
  },
  {
    path: '/web-development',
    label: 'Web development',
    kind: 'service',
    footerService: true,
    navOrder: 100,
    llms: 'core',
    llmsSummary:
      'React, Next.js, APIs, CMS, Core Web Vitals, CRM integrations, and production support.',
    priority: 0.9,
    seo: {
      title: 'Web development: React, Next.js & APIs',
      description:
        'React, Next.js, CMS, API, and ecommerce web development with Core Web Vitals work, CRM integrations, and long-term production support.',
      keywords:
        'web development services, React development company, Next.js development, product engineering agency, API development, CMS development, Core Web Vitals optimization, web development Gautam Buddha Nagar India',
      image: '/img/webdevelopment.png',
      imageAlt: 'Web development and product engineering services',
    },
  },
  {
    path: '/ecommerce-development',
    label: 'Ecommerce development',
    kind: 'service',
    footerService: true,
    navOrder: 110,
    llms: 'core',
    llmsSummary:
      'Ecommerce storefronts, checkout, payments, operations tooling, Magento, and WooCommerce.',
    priority: 0.9,
    seo: {
      title: 'Ecommerce development for global brands',
      description:
        'Ecommerce development for catalog UX, checkout optimization, payments, shipping, admin workflows, and scalable store operations.',
      keywords:
        'ecommerce development company, online store development, checkout optimization, ecommerce integrations, B2B ecommerce development, global ecommerce agency',
    },
  },
  {
    path: '/magento-development',
    label: 'Magento development',
    kind: 'service',
    footerService: true,
    navOrder: 120,
    llms: 'core',
    llmsSummary: 'Magento 2 and Adobe Commerce builds, integrations, and store operations.',
    priority: 0.9,
    seo: {
      title: 'Magento & Adobe Commerce development',
      description:
        'Magento 2 and Adobe Commerce builds, themes, modules, performance tuning, ERP/CRM/payment integrations, upgrades, and support.',
      keywords:
        'Magento developer, Adobe Commerce agency, Magento 2 development, ecommerce customization, Magento performance optimization, Magento integrations',
    },
  },
  {
    path: '/mobile-app',
    label: 'Mobile apps',
    kind: 'service',
    footerService: true,
    navOrder: 130,
    llms: 'core',
    llmsSummary: 'React Native apps for iOS and Android, store publishing, and API-connected workflows.',
    priority: 0.8,
    seo: {
      title: 'React Native mobile app development',
      description:
        'React Native apps for iOS and Android—customer apps, field tools, push notifications, API integrations, store publishing, and ongoing support.',
      keywords:
        'React Native developer India, mobile app development Gautam Buddha Nagar, Android iOS app agency, cross-platform apps, React Native company, mobile API integration',
    },
  },
  {
    path: '/ai-automation',
    label: 'AI automation',
    kind: 'landing',
    footerService: true,
    navOrder: 140,
    llms: 'core',
    llmsSummary:
      'AI workflows, chatbots, voice assistants, CRM automation, and operational reporting.',
    priority: 0.85,
    seo: {
      title: 'AI automation & chatbot development',
      description:
        'AI workflows, chatbots, voice assistants, and CRM-connected automation with guardrails, analytics, and production-ready integrations for global teams.',
      keywords:
        'AI automation services, chatbot development company, voice assistant development, LLM workflow integration, CRM automation, AI support triage',
    },
  },
  {
    path: '/wordpress-development',
    label: 'WordPress development',
    kind: 'landing',
    footerService: true,
    navOrder: 150,
    llms: 'core',
    llmsSummary: 'WordPress, Roots Sage, ACF content systems, performance, and maintenance.',
    priority: 0.85,
    seo: {
      title: 'WordPress & Roots Sage development',
      description:
        'Custom WordPress and Roots Sage themes for content-rich sites—flexible sections, performance-minded delivery, and long-term support.',
      keywords:
        'WordPress development agency, Roots Sage developer, custom WordPress theme, ACF page builder, WordPress maintenance, headless WordPress',
    },
  },
  {
    path: '/website-maintenance',
    label: 'Website maintenance',
    kind: 'landing',
    footerService: true,
    navOrder: 160,
    llms: 'core',
    llmsSummary:
      'Hosting support, SSL, backups, monitoring, Core Web Vitals, and ongoing improvements.',
    priority: 0.85,
    seo: {
      title: 'Website maintenance & performance',
      description:
        'Website maintenance with hosting support, SSL, backups, updates, monitoring, Core Web Vitals improvements, and ongoing optimization for production sites.',
      keywords:
        'website maintenance services, managed website support, Core Web Vitals optimization, hosting support, SSL backups monitoring, website performance audit',
    },
  },
  {
    path: '/web-design',
    label: 'Web design',
    kind: 'service',
    navOrder: 170,
    llms: 'core',
    llmsSummary: 'UX and interface design for marketing sites and product surfaces.',
    priority: 0.8,
    seo: {
      title: 'Web design services — UX, UI & conversion-focused layouts',
      description:
        'User-centered web design: information architecture, responsive UI, accessibility-minded patterns, and conversion-focused landing pages aligned with your brand and analytics goals.',
      keywords:
        'web design company Gautam Buddha Nagar, UX design services, responsive website design, landing page design, accessible web design',
    },
  },
  {
    path: '/marketplace-cataloging',
    label: 'Marketplace cataloging',
    kind: 'landing',
    navOrder: 200,
    llms: 'specialized',
    priority: 0.7,
    seo: {
      title: 'Marketplace cataloging: Amazon & Flipkart',
      description:
        'Marketplace cataloging for product data cleanup, template mapping, SEO content, image checks, bulk uploads, and listing optimization.',
      keywords:
        'marketplace cataloging services, Amazon listing services, Flipkart catalog management, product listing optimization, marketplace SEO, bulk upload support',
    },
  },
  {
    path: '/education-website-development',
    label: 'Education website development',
    kind: 'landing',
    navOrder: 210,
    llms: 'specialized',
    priority: 0.7,
    seo: {
      title: 'Education websites & course discovery',
      description:
        'Education website development with WordPress/Sage, course discovery, campaign pages, CMS sections, forms, search, and ongoing institutional support.',
      keywords:
        'education website development, university website development, course discovery platform, admissions campaign pages, WordPress education CMS, Sage education websites',
    },
  },
  {
    path: '/erp-development',
    label: 'ERP development',
    kind: 'landing',
    navOrder: 220,
    llms: 'specialized',
    priority: 0.7,
    seo: {
      title: 'Custom ERP for ecommerce operations',
      description:
        'Custom ERP for seller portals, multi-channel integrations, inventory, orders, reconciliation, accounting exports, and ops analytics.',
      keywords:
        'custom ERP development, ecommerce ERP software, marketplace ERP integration, inventory management system, order reconciliation software, seller admin portal development',
    },
  },
  {
    path: '/form-crm-integrations',
    label: 'Form & CRM integrations',
    kind: 'landing',
    navOrder: 230,
    llms: 'specialized',
    priority: 0.7,
    seo: {
      title: 'Form & CRM integration services',
      description:
        'Form and CRM integrations for FormAssembly, Marketo, WPForms, Calendly, HubSpot, and Salesforce—with embeds and conversion tracking.',
      keywords:
        'FormAssembly integration, Marketo form integration, WPForms developer, HubSpot Salesforce integration, CRM form integration, education lead capture forms',
    },
  },
  {
    path: '/education-search-platforms',
    label: 'Education search platforms',
    kind: 'landing',
    navOrder: 240,
    llms: 'specialized',
    priority: 0.7,
    seo: {
      title: 'Education search: Algolia & SiteSearch360',
      description:
        'Education search platforms for course discovery, faceted filters, autocomplete, Algolia, SiteSearch360, Fuse.js, and WordPress indexing.',
      keywords:
        'education search platform, course search development, Algolia WordPress developer, SiteSearch360 integration, faceted search education website, course discovery filters',
    },
  },
  {
    path: '/woocommerce-payment-crm-integrations',
    label: 'WooCommerce payments & CRM',
    kind: 'landing',
    navOrder: 250,
    llms: 'specialized',
    priority: 0.7,
    seo: {
      title: 'WooCommerce payments & CRM integrations',
      description:
        'WooCommerce payment and CRM integrations for Flywire, invoices, HubSpot, Salesforce, custom checkout, bundles, discounts, and PDF invoices.',
      keywords:
        'WooCommerce payment gateway integration, Flywire WooCommerce, HubSpot WooCommerce integration, Salesforce WooCommerce integration, custom WooCommerce checkout, WooCommerce invoice payments',
    },
  },
  {
    path: '/analytics-tag-management',
    label: 'Analytics & tag management',
    kind: 'landing',
    navOrder: 260,
    llms: 'specialized',
    priority: 0.7,
    seo: {
      title: 'Analytics, GTM & tag management',
      description:
        'Analytics and tag management for GTM, dataLayer events, Hotjar, Clarity, and conversion QA—so measurement stays accurate in production.',
      keywords:
        'GTM implementation services, dataLayer event tracking, Hotjar setup, Microsoft Clarity setup, conversion tracking implementation, tag management agency',
    },
  },
  {
    path: '/privacy-translation-widgets',
    label: 'Privacy, translation & widgets',
    kind: 'landing',
    navOrder: 270,
    llms: 'specialized',
    priority: 0.7,
    seo: {
      title: 'Privacy, translation & website widgets',
      description:
        'Privacy, translation, review, chat, and education widget integrations—OneTrust, GTranslate, Trustpilot, Unibuddy, Maps, and related embeds.',
      keywords:
        'OneTrust WordPress integration, GTranslate WordPress developer, Trustpilot widget integration, Unibuddy integration, Discover Uni widget, education website widgets',
    },
  },
  {
    path: '/portfolio/classic',
    label: 'Portfolio by stack',
    kind: 'portfolio',
    llms: false,
    priority: 0.6,
    seo: {
      title: 'Portfolio by stack: Magento, WP, Laravel',
      description:
        'Browse case studies by technology: Magento, PHP, Laravel, CodeIgniter, and WordPress projects with previews and context.',
      keywords:
        'Magento portfolio, WordPress projects, Laravel case studies, PHP web development gallery, CodeIgniter projects India',
    },
  },
  {
    path: '/portfolio/classic/magento2',
    label: 'Magento 2 portfolio',
    kind: 'portfolio',
    llms: false,
    priority: 0.5,
    seo: {
      title: 'Magento 2 ecommerce portfolio',
      description:
        'Magento 2 ecommerce projects including store builds, catalog scaling, and checkout optimization.',
      keywords: 'Magento 2 portfolio, Magento ecommerce projects, Magento case studies India',
    },
  },
  {
    path: '/portfolio/classic/php',
    label: 'PHP portfolio',
    kind: 'portfolio',
    llms: false,
    priority: 0.5,
    seo: {
      title: 'PHP web development portfolio',
      description:
        'PHP websites and custom applications for startups and enterprises, with project previews and context.',
      keywords: 'PHP portfolio, PHP web development projects, PHP case studies India',
    },
  },
  {
    path: '/portfolio/classic/laravel',
    label: 'Laravel portfolio',
    kind: 'portfolio',
    llms: false,
    priority: 0.5,
    seo: {
      title: 'Laravel custom application portfolio',
      description:
        'Laravel application and API projects with screenshots, technology context, and delivery highlights.',
      keywords: 'Laravel portfolio, Laravel development projects, Laravel case studies India',
    },
  },
  {
    path: '/portfolio/classic/codeigniter',
    label: 'CodeIgniter portfolio',
    kind: 'portfolio',
    llms: false,
    priority: 0.5,
    seo: {
      title: 'CodeIgniter PHP app portfolio',
      description:
        'CodeIgniter web applications including maintenance upgrades and feature delivery.',
      keywords: 'CodeIgniter portfolio, CodeIgniter projects, PHP framework case studies',
    },
  },
  {
    path: '/portfolio/classic/wordpress',
    label: 'WordPress portfolio',
    kind: 'portfolio',
    llms: false,
    priority: 0.5,
    seo: {
      title: 'WordPress business site portfolio',
      description:
        'WordPress websites and marketing platforms with large previews and project details.',
      keywords: 'WordPress portfolio, WordPress agency projects, WordPress case studies India',
    },
  },
];
